import * as React from 'react';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import { Autocomplete, Box, TextField } from '@mui/material';
import { useGetDataApi } from '@crema/hooks/APIHooks';
import type {
  EnterprisePropsType,
  MappingType,
} from '@crema/models/provisionify/Enterprise';
import AppLoader from '@crema/components/AppLoader';
import { useAuthUser } from '@crema/hooks/AuthHooks';
import jwtAxios from '@crema/services/auth/JWT';
import { MuiTelInput, MuiTelInputInfo } from 'mui-tel-input';
import newFeatureIcon from '../../../../assets/new/newicon.png';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    paddingLeft: theme.spacing(8),
    paddingRight: theme.spacing(8),
  },
  '& .MuiDialogActions-root': {
    paddingLeft: theme.spacing(8),
    paddingRight: theme.spacing(8),
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
}));

export interface DialogTitleProps {
  id: string;
  children?: React.ReactNode;
  onClose: () => void;
}

function BootstrapDialogTitle(props: DialogTitleProps) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle
      sx={{ m: 0, px: 8, pt: 8, pb: 5, width: 562, maxWidth: '100%' }}
      {...other}
    >
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
}

interface EnterpriseSelectType {
  label: string;
  enterpriseId: string;
  isMigrated: boolean;
}
interface NewMappingDialogProps {
  open: boolean;
  handleClose: () => void;
  handleSubmit: (
    status: boolean,
    data: MappingType | string,
    enterpriseId?: string
  ) => void;
  selectedEnterpriseId?: string;
}

export default function NewMappingDialog(props: NewMappingDialogProps) {
  const { user } = useAuthUser();
  let enterprisesResponseData: any[] = [];
  let loadingResponse = false;
  let enterpriseAdmin = user.roles.includes('ENTERPRISE_ADMIN');

  if(!enterpriseAdmin){
    let [{ apiData: enterprisesResponse, loading }] = useGetDataApi<
      EnterprisePropsType[]
    >(`/csp/enterprises/${user.cspUserId}?pagination=false`);
    enterprisesResponseData = enterprisesResponse;
    loadingResponse = loading;
  }

  var defaultMmsSelection:
    | boolean
    | ((prevState: boolean) => boolean)
    | undefined = undefined;
  if (props.selectedEnterpriseId != undefined) {
    const [{ apiData: enterpriseResponse }] =
      useGetDataApi<EnterprisePropsType>(
        `/enterprise/getenterprise/${props.selectedEnterpriseId}`
      );
    defaultMmsSelection = enterpriseResponse?.defaultMmsSelection;
  }

  const [name, setName] = React.useState('');
  const [phoneNumber, setPhoneNumber] = React.useState('');
  const [messagingAppId, setMessagingAppId] = React.useState('');
  const [enterprise, setEnterprise] =
    React.useState<EnterpriseSelectType | null>({
      label: '',
      enterpriseId: props.selectedEnterpriseId
        ? (props.selectedEnterpriseId as string)
        : '',
      isMigrated: false,
    });
  const [mmsEnabled, setMmsEnabled] = React.useState(false);

  React.useEffect(() => {
    if (defaultMmsSelection == true) {
      setMmsEnabled(defaultMmsSelection);
    }
  }, [defaultMmsSelection]);

  const handleSubmit = () => {

    if(name == ''){
      props.handleSubmit(false, `Please enter a Name, Name should not be blank`);
      return;
    }
    if(messagingAppId == ''){
      props.handleSubmit(false, `Please enter a Cisco Id, Cisco Id should not be blank`);
      return;
    }
    if(!isValidEmail(messagingAppId)){
      props.handleSubmit(false, `Cisco Id is missing/incorrect`);
      return;
    }
    if(user.mmsFeature == 'Disabled' && messagingAppId.includes(';')){
      console.log('error: Team texting is not supported for your tenant. Please contact your admin.');
      props.handleSubmit(false, `Team texting is not supported for your tenant. Please contact your admin.`);
      return;
    }
    if(user.mmsFeature == 'Partial' && mmsEnabled == false && messagingAppId.includes(';')){
      console.log('error: Team texting requires mms to be enabled. Please select the MMS Enable check box.');
      props.handleSubmit(false, `Team texting requires mms to be enabled. Please select the MMS Enable check box.`);
      return;
    }

    if (enterprise?.enterpriseId) {
      const createMappingRequestData: any = {
        
        name,
        phoneNumber,
        messagingAppId,
        messagingAppType: 'Webex',
        mmsEnabled: user.mmsFeature == 'Strict' ? true : mmsEnabled,
      };
      if (user.roles.includes('SUPER_ADMIN')) {
        createMappingRequestData.tenantId = user.email;
      }
      jwtAxios
        .post(
          `/mapping/add/${enterprise?.enterpriseId}`,
          createMappingRequestData
        )
        .then(({ data }: { data: MappingType }) => {
          props.handleSubmit(true, data, enterprise?.enterpriseId);
          setName('');
          setPhoneNumber('');
          setMessagingAppId('');
          setEnterprise({
            label: '',
            enterpriseId: props.selectedEnterpriseId
              ? (props.selectedEnterpriseId as string)
              : '',
            isMigrated: false,
          });
          setMmsEnabled(false);
        })
        .catch((error: any) => {
          props.handleSubmit(false, error.response.data.message);
        });
    } else {
      props.handleSubmit(false, 'Please select an enterprise');
    }
  };

  function isValidEmail(email: string) {
    const emailPattern = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))(?:\s*;\s*(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,})))*$/;
    // Test the provided email against the pattern
    return emailPattern.test(email);
  }

  return (
    <>
      {loadingResponse ? (
        <AppLoader />
      ) : (
        <BootstrapDialog
          onClose={props.handleClose}
          aria-labelledby="customized-dialog-title"
          open={props.open}
        >
          <BootstrapDialogTitle
            id="customized-dialog-title"
            onClose={props.handleClose}
          >
            <Typography sx={{ fontSize: 28, fontWeight: 500, marginBottom: 3 }}>
              Create a mapping
            </Typography>
            <Typography>
              Please enter the mapping information below to provision a new
              user.
            </Typography>
          </BootstrapDialogTitle>
          <DialogContent dividers>
            <Box sx={{ mb: { xs: 5, xl: 8 } }}>
              <TextField
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Hunter Gon"
                label="name"
                variant="outlined"
                sx={{
                  width: '100%',
                  '& .MuiInputBase-input': {
                    fontSize: 14,
                  },
                }}
                autoFocus
              />
            </Box>
            <Box sx={{ mb: { xs: 5, xl: 8 } }}>
              {/* <TextField
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="+7039125921"
                label="Number"
                variant="outlined"
                sx={{
                  width: '100%',
                  '& .MuiInputBase-input': {
                    fontSize: 14,
                  },
                }}
              /> */}
              <MuiTelInput
                forceCallingCode
                defaultCountry="US"
                onlyCountries={['US', 'CA']}
                placeholder="267 345 3577"
                label="Number"
                variant="outlined"
                sx={{
                  width: '100%',
                  '& .MuiInputBase-input': {
                    fontSize: 14,
                  },
                }}
                value={phoneNumber}
                onChange={(value: string, info: MuiTelInputInfo) =>
                  setPhoneNumber(info.numberValue || '')
                }
              />
            </Box>
            <Box sx={{ mb: { xs: 5, xl: 8 } }}>
              <TextField
                value={messagingAppId}
                onChange={(e) => setMessagingAppId(e.target.value)}
                placeholder="something@cisco.com"
                label="Cisco ID"
                variant="outlined"
                sx={{
                  width: '100%',
                  '& .MuiInputBase-input': {
                    fontSize: 14,
                  },
                }}
              />
              {user.mmsFeature != 'Disabled' ? (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <img src={newFeatureIcon} alt="New Feature" style={{ marginRight: '0.5rem', width: '40px', height: '40px' }} />
              <Typography component="pre">
                {'Please separate multiple Cisco IDs with a semicolon ( ; ) \n(e.g., support@cisco.com;sales@cisco.com).'}
              </Typography>
            </div>
              ) : null}
            </Box>
            {!props.selectedEnterpriseId && !enterpriseAdmin && (
              <Box>
                <Autocomplete
                  disablePortal
                  id="combo-box-demo"
                  options={enterprisesResponseData ? enterprisesResponseData.map((item) => ({
                    label: item.name,
                    enterpriseId: item.enterpriseId,
                    isMigrated: item.isMigrated,
                  })): []}
                  getOptionDisabled={(option) => option.isMigrated === true}
                  renderOption={(props, option) => (
                    <li {...props} style={{
                      color: option.isMigrated ? '#888' : 'inherit',
                      backgroundColor: option.isMigrated ? '#f5f5f5' : 'inherit',
                      cursor: option.isMigrated ? 'not-allowed' : 'pointer',
                    }}
                      title={option.isMigrated ? 'This enterprise is migrated to Platform' : ''}
                    >
                      {option.label}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label="Enterprise" />
                  )}
                  value={enterprise}
                  onChange={(
                    event: any,
                    newValue: EnterpriseSelectType | null
                  ) => {
                    setEnterprise(newValue);
                  }}
                />
              </Box>
            )}
            &nbsp;
            <Box sx={{ mb: { xs: 5, xl: 8 } }}>
              <input
                type="checkbox"
                onChange={(e) => setMmsEnabled(e.target.checked)}
                hidden={user.mmsFeature != 'Partial'}
                defaultChecked={defaultMmsSelection}
              />
              <label id="mmsEnabled" hidden={user.mmsFeature != 'Partial'}>
                {' '}
                MMS Enable
              </label>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              color="primary"
              onClick={props.handleClose}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleSubmit()}
            >
              Submit
            </Button>
          </DialogActions>
        </BootstrapDialog>
      )}
    </>
  );
}
